import React, { useEffect, useState } from "react";
import { useLicense } from "../../context/licenseContext";
import { useNavigate } from "react-router-dom";
import { v4 as uuidv4 } from "uuid";
import apiClient from "../../api/apiClient";
import { useToast } from "../../context/toastContext";
import Loading from "../../components/Loading/Loading";
import LabOrderSamples from "./LabOrderSamples";
import PdfViewerModal from "../../components/PdfViewerModal/PdfViewerModal";
import capitalize from "../../utils/capitalize";
import useUnsavedChanges from "../../hooks/useUnsavedChanges";
import "./LabOrders.css";
import { getPanelStatus } from "../../utils/coaPanelStatus";
import OrderSuccessModal from "../../components/OrderSuccessModal/OrderSuccessModal";

const createNewSample = () => ({
    id: uuidv4(),
    name: "",
    batch_number: "",
    matrix: "F",
    weight_grams: "",
    weight_unit: "g",
    sku: "",
    tests: {
        cannabinoids: false,
        pesticides: false,
        heavy_metals: false,
        mycotoxins: false,
        yeast_and_mold: false, 
        microbials: false,
        moisture_content: false,
        water_activity: false,
        foreign_matter: false,
        terpenes: false,
    },
});

const STATUS_LABELS = {
    ordered: "Ordered",
    in_progress: "In Progress",
    completed: "Completed",
    rejected: "Rejected",
    pending: "Pending",
    cancelled: "Cancelled",
};

const formatDate = (dateStr) => {
    if (!dateStr || dateStr.startsWith("1970-01-01")) return "—";
    return new Date(dateStr).toLocaleDateString("en-US", {
        year: "numeric",
        month: "short",
        day: "numeric",
    });
};

const LabOrders = ({ userId, setHeaderTitle }) => {
    const { currentLicense } = useLicense();
    const toast = useToast();
    const navigate = useNavigate();

    const [permissions, setPermissions] = useState(null);
    const [permsLoading, setPermsLoading] = useState(true);

    const [ordersList, setOrdersList] = useState([]);
    const [listLoading, setListLoading] = useState(false);
    const [cocDownloadingId, setCocDownloadingId] = useState(null);

    const [showForm, setShowForm] = useState(false);
    const [formLoading, setFormLoading] = useState(false);

    const [productName, setProductName] = useState("");
    const [seedToSaleNumber, setSeedToSaleNumber] = useState("");
    const [comments, setComments] = useState("");
    const [characterCount, setCharacterCount] = useState(500);
    const [samples, setSamples] = useState([createNewSample()]);
    const [formError, setFormError] = useState("");

    const [availableLabs, setAvailableLabs] = useState([]);
    const [selectedLabId, setSelectedLabId] = useState("");
    const [labsLoading, setLabsLoading] = useState(false);

    const [productMode, setProductMode]     = useState("new"); // 'new' | 'existing'
    const [userCoas, setUserCoas]             = useState([]);
    const [selectedCoaId, setSelectedCoaId]   = useState("");
    const [selectedCoa, setSelectedCoa]       = useState(null);

    const [searchTerm, setSearchTerm] = useState("");
    const [allOrders, setAllOrders] = useState([]);

    const [successEmail, setSuccessEmail] = useState(null);

    // Set of collapsed orderIds (empty = all expanded)
    const [collapsedOrders, setCollapsedOrders] = useState(new Set());

    // PDF viewer
    const [pdfViewerUrls, setPdfViewerUrls] = useState([]);
    const [showPdfViewer, setShowPdfViewer] = useState(false);

    const hasOrderFormData = showForm && !!(productName || seedToSaleNumber || comments || selectedLabId);
    useUnsavedChanges(hasOrderFormData, { message: "You have an unsaved lab order form. Leave without submitting?" });

    useEffect(() => {
        if (setHeaderTitle) setHeaderTitle("Lab Orders");
    }, [setHeaderTitle]);

    useEffect(() => {
        if (!currentLicense?.licenseNo) return;
        setPermsLoading(true);
        apiClient
            .get(`/permissions/my-permissions?licenseNo=${currentLicense.licenseNo}`)
            .then((res) => setPermissions(res.data))
            .catch(() =>
                setPermissions({ can_view_lab_tests: 1, can_order_tests: 1 })
            )
            .finally(() => setPermsLoading(false));
    }, [currentLicense]);

    const fetchAvailableLabs = async () => {
        if (!currentLicense?.licenseNo) return;
        setLabsLoading(true);
        try {
            const res = await apiClient.get(`/lab-orders/available-labs?licenseNo=${currentLicense.licenseNo}`);
            setAvailableLabs(Array.isArray(res.data) ? res.data : []);
        } catch (err) {
            console.error("Error fetching available labs:", err);
            toast.error(err.userMessage || "Failed to load available labs");
            setAvailableLabs([]);
        } finally {
            setLabsLoading(false);
        }
    };

    const fetchOrders = async () => {
        if (!currentLicense?.licenseNo || !userId) return;
        setListLoading(true);
        try {
            const res = await apiClient.get(
                `/lab-orders/list?licenseNo=${currentLicense.licenseNo}`
            );
            const orders = res.data || [];
            setAllOrders(orders);
            setOrdersList(orders);
            setSearchTerm("");
        } catch (err) {
            console.error("Error fetching orders:", err);
            toast.error(err.userMessage || "Failed to load lab orders");
            setOrdersList([]);
            setAllOrders([]);
        } finally {
            setListLoading(false);
        }
    };

    useEffect(() => {
        if (permissions && permissions.can_view_lab_tests !== 0) {
            fetchOrders();
        }
    }, [currentLicense, userId, permissions]);

    const handleSearchChange = (e) => {
        const value = e.target.value.toLowerCase();
        setSearchTerm(e.target.value);
        if (value) {
            setOrdersList(
                allOrders.filter((order) =>
                    order.samples?.some(
                        (s) =>
                            s.sampleName?.toLowerCase().includes(value) ||
                            s.batch_number?.toLowerCase().includes(value)
                    )
                )
            );
        } else {
            setOrdersList(allOrders);
        }
    };

    const openPdfViewer = (urls) => {
        setPdfViewerUrls(Array.isArray(urls) ? urls : [urls]);
        setShowPdfViewer(true);
    };

    const toggleOrder = (orderId) => {
        setCollapsedOrders((prev) => {
            const next = new Set(prev);
            next.has(orderId) ? next.delete(orderId) : next.add(orderId);
            return next;
        });
    };

    const fetchUserCoas = async () => {
        if (!currentLicense?.licenseNo) return;
        try {
            const res = await apiClient.get(`/coas/coa-by-license?licenseNo=${currentLicense.licenseNo}`);
            setUserCoas(Array.isArray(res.data) ? res.data : []);
        } catch {
            setUserCoas([]);
        }
    };

    const handleCoaSelect = (coaId) => {
        setSelectedCoaId(coaId);
        const coa = userCoas.find((c) => String(c.id) === String(coaId)) || null;
        setSelectedCoa(coa);
        if (coa) setProductName(coa.product_name || "");
    };

    const resetForm = () => {
        setProductName("");
        setSeedToSaleNumber("");
        setComments("");
        setCharacterCount(500);
        setSamples([createNewSample()]);
        setFormError("");
        setSelectedLabId("");
        setProductMode("new");
        setSelectedCoaId("");
        setSelectedCoa(null);
        setUserCoas([]);
    };

    // The confirmation email (with the COC form attached) is sent after the order is saved, so a
    // failed send must not leave the customer without the form — this fetches it again on demand.
    const downloadCoc = async (orderId) => {
        setCocDownloadingId(orderId);
        try {
            const res = await apiClient.post("/lab-orders/get-coc", { orderId }, { responseType: "blob" });
            const blobUrl = URL.createObjectURL(res.data);
            const link = document.createElement("a");
            link.href = blobUrl;
            link.download = "chain-of-custody.xlsx";
            document.body.appendChild(link);
            link.click();
            link.remove();
            URL.revokeObjectURL(blobUrl);
        } catch (err) {
            toast.error(err.userMessage || "Failed to download the chain of custody form");
        } finally {
            setCocDownloadingId(null);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setFormError("");

        if (characterCount < 0) {
            setFormError("Comments cannot exceed 500 characters.");
            return;
        }

        const samplesPayload = samples.map((sample) => ({
            name: sample.name,
            batch_number: sample.batch_number,
            matrix: sample.matrix,
            tests: Object.entries(sample.tests)
                .filter(([, checked]) => checked)
                .map(([name]) => name),
            ...(productMode === "new"
                ? {
                    weight_grams: sample.weight_grams,
                    weight_unit: sample.weight_unit,
                    sku: sample.sku?.trim() || undefined,
                }
                : {}),
        }));

        if (!samplesPayload.some((s) => s.tests.length > 0)) {
            setFormError("Please add at least one test to a sample.");
            return;
        }

        if (!selectedLabId) {
            setFormError("Please select a lab partner.");
            return;
        }

        if (productMode === "new") {
            if (samples.some((s) => !String(s.name).trim())) {
                setFormError("Each sample needs a name — it becomes that sample's product name.");
                return;
            }
            if (samples.some((s) => !String(s.weight_grams).trim())) {
                setFormError("Batch weight is required for each sample so its product can be added to inventory.");
                return;
            }
        }

        setFormLoading(true);
        try {
            const res = await apiClient.post("/lab-orders/create-order", {
                seedToSaleNumber,
                comments,
                licenseNo: currentLicense.licenseNo,
                samples: samplesPayload,
                partnerId: parseInt(selectedLabId, 10),
                ...(selectedCoaId ? { coaId: selectedCoaId, productName } : {}),
            });
            setShowForm(false);
            resetForm();
            await fetchOrders();
            setSuccessEmail(res.data.email || "");
        } catch (err) {
            console.error("Error creating order:", err);
            setFormError(err.response?.data?.error || "Failed to create order.");
        } finally {
            setFormLoading(false);
        }
    };

    if (permsLoading) return <Loading position="center" />;

    if (permissions?.can_view_lab_tests === 0) {
        return (
            <div className="lab-orders-page">
                <div className="lab-orders-permission-denied">
                    <h2>Access Restricted</h2>
                    <p>You don't have permission to view lab orders.</p>
                </div>
            </div>
        );
    }

    return (
        <div className="lab-orders-page">
            {successEmail !== null && (
                <OrderSuccessModal
                    email={successEmail}
                    onClose={() => setSuccessEmail(null)}
                />
            )}

            {showPdfViewer && (
                <PdfViewerModal
                    urls={pdfViewerUrls}
                    onClose={() => setShowPdfViewer(false)}
                />
            )}

            <div className="lab-orders-header">
                <h1 className="lab-orders-title">Lab Orders</h1>
                {permissions?.can_order_tests !== 0 && (
                    <button
                        className="lab-orders-new-btn"
                        data-tutorial="lab-order-new-btn"
                        onClick={() => {
                            const next = !showForm;
                            setShowForm(next);
                            if (next) fetchAvailableLabs();
                            else resetForm();
                        }}
                    >
                        {showForm ? "Cancel" : "+ New Order"}
                    </button>
                )}
            </div>

            {showForm && (
                <div className="lab-orders-form-panel">
                    <h2 className="lab-orders-form-title">Create New Order</h2>
                    <p className="lab-orders-form-desc">
                        Fill in the details below to create a lab order. You will receive a COC form by email.
                    </p>
                    {formLoading && <Loading position="center" />}
                    {!formLoading && (
                        <form className="lab-orders-form" onSubmit={handleSubmit}>
                            {/* Mode toggle */}
                            <div className="lo-mode-toggle">
                                <button
                                    type="button"
                                    className={`lo-mode-btn${productMode === "new" ? " lo-mode-btn--active" : ""}`}
                                    onClick={() => {
                                        setProductMode("new");
                                        setSelectedCoaId("");
                                        setSelectedCoa(null);
                                        setProductName("");
                                    }}
                                >
                                    New Product
                                </button>
                                <button
                                    type="button"
                                    className={`lo-mode-btn${productMode === "existing" ? " lo-mode-btn--active" : ""}`}
                                    onClick={() => {
                                        setProductMode("existing");
                                        fetchUserCoas();
                                    }}
                                >
                                    Existing COA
                                </button>
                            </div>

                            {productMode === "existing" && (
                                <>
                                    <label className="lo-label" htmlFor="coaSelect">
                                        Select COA *
                                    </label>
                                    <select
                                        id="coaSelect"
                                        className="lo-input"
                                        value={selectedCoaId}
                                        onChange={(e) => handleCoaSelect(e.target.value)}
                                        required
                                    >
                                        <option value="">-- Select a COA --</option>
                                        {userCoas.map((c) => (
                                            <option key={c.id} value={c.id}>
                                                {c.product_name}
                                            </option>
                                        ))}
                                    </select>
                                </>
                            )}

                            {productMode === "existing" && (
                                <>
                                    <label className="lo-label" htmlFor="productName">
                                        Product/Project Name *
                                    </label>
                                    <input
                                        id="productName"
                                        className="lo-input"
                                        type="text"
                                        required
                                        placeholder="Product Name"
                                        value={productName}
                                        onChange={(e) => setProductName(e.target.value)}
                                        readOnly={!!selectedCoa}
                                    />
                                </>
                            )}

                            {productMode === "new" && (
                                <>
                                    <p className="lo-label-hint">
                                        Each sample below becomes its own inventory product, named after that sample —
                                        give each one a name, batch weight, and (optionally) a SKU.
                                    </p>
                                </>
                            )}

                            <label className="lo-label" htmlFor="seedToSaleNumber">
                                Seed to Sale Number
                            </label>
                            <input
                                id="seedToSaleNumber"
                                className="lo-input"
                                type="text"
                                placeholder="Seed to Sale Number"
                                value={seedToSaleNumber}
                                onChange={(e) => setSeedToSaleNumber(e.target.value)}
                            />
                            <label className="lo-label" htmlFor="labPartner">Lab Partner *</label>
                            {labsLoading ? (
                                <Loading position="inline" />
                            ) : availableLabs.length === 0 ? (
                                <p className="lo-error">No eligible lab partners available for your license type.</p>
                            ) : (
                                <select
                                    id="labPartner"
                                    className="lo-input"
                                    required
                                    value={selectedLabId}
                                    onChange={(e) => setSelectedLabId(e.target.value)}
                                >
                                    <option value="">-- Select Lab Partner --</option>
                                    {availableLabs.map((lab) => (
                                        <option key={lab.id} value={lab.id}>
                                            {lab.name} ({lab.state || "N/A"})
                                        </option>
                                    ))}
                                </select>
                            )}
                            <hr className="lo-divider" />
                            <LabOrderSamples
                                samples={samples}
                                setSamples={setSamples}
                                createNewSample={createNewSample}
                                productMode={productMode}
                                availableTests={
                                    productMode === "existing" && selectedCoa
                                        ? Object.entries(getPanelStatus(selectedCoa))
                                              .filter(([, { met }]) => !met)
                                              .map(([key]) =>
                                                  key === "moisture" ? "moisture_content" : key
                                              )
                                        : null
                                }
                            />
                            <div className="lo-comments">
                                <label className="lo-label" htmlFor="comments">
                                    Additional comments and/or description
                                </label>
                                <textarea
                                    id="comments"
                                    className="lo-textarea"
                                    placeholder="Comments &/or Description"
                                    rows={5}
                                    maxLength={500}
                                    value={comments}
                                    onChange={(e) => {
                                        setComments(e.target.value);
                                        setCharacterCount(500 - e.target.value.length);
                                    }}
                                />
                                <p className={characterCount <= 0 ? "lo-char-red" : "lo-char"}>
                                    {characterCount} character{characterCount === 1 ? "" : "s"} remaining
                                </p>
                            </div>
                            {formError && <p className="lo-error">{formError}</p>}
                            <button type="submit" className="lo-submit-btn" disabled={labsLoading || availableLabs.length === 0 || !selectedLabId}>
                                Create Order
                            </button>
                        </form>
                    )}
                </div>
            )}

            <div className="lab-orders-list-section" data-tutorial="lab-orders-list">
                <div className="lab-orders-search-row">
                    <input
                        type="text"
                        className="lo-search"
                        placeholder="Search by sample name or batch number..."
                        value={searchTerm}
                        onChange={handleSearchChange}
                    />
                    <span className="lo-count">
                        {ordersList.length} order{ordersList.length !== 1 ? "s" : ""}
                    </span>
                </div>

                {listLoading ? (
                    <Loading position="center" />
                ) : ordersList.length === 0 ? (
                    <div className="lo-empty">
                        <p>No lab orders found.</p>
                        {permissions?.can_order_tests !== 0 && (
                            <button
                                className="lab-orders-new-btn"
                                onClick={() => {
                                    setShowForm(true);
                                    fetchAvailableLabs();
                                }}
                            >
                                + Create Your First Order
                            </button>
                        )}
                    </div>
                ) : (
                    <div className="lo-cards">
                        {ordersList.map((order) => {
                            const isCollapsed = collapsedOrders.has(order.orderId);
                            const sampleList = order.samples || [];
                            return (
                                <div key={order.orderId} className="lo-card">
                                    <div
                                        className="lo-card-header"
                                        onClick={() => toggleOrder(order.orderId)}
                                    >
                                        <span className={`lo-chevron${isCollapsed ? "" : " lo-chevron--open"}`}>
                                            <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                                                <path d="M6 4l4 4-4 4" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
                                            </svg>
                                        </span>
                                        <span className={`lo-status lo-status--${order.status || "ordered"}`}>
                                            {STATUS_LABELS[order.status] || capitalize(order.status || "ordered")}
                                        </span>
                                        <span className="lo-card-meta">{formatDate(order.createdAt)}</span>
                                        <span className="lo-card-dot">&middot;</span>
                                        <span className="lo-card-meta">
                                            {sampleList.length} sample{sampleList.length !== 1 ? "s" : ""}
                                        </span>
                                        <div className="lo-card-spacer" />
                                        {(order.status === "ordered" || order.status === "in_progress") && (
                                            <button
                                                className="lo-action-btn"
                                                disabled={cocDownloadingId === order.orderId}
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    downloadCoc(order.orderId);
                                                }}
                                            >
                                                {cocDownloadingId === order.orderId ? "Downloading…" : "Download COC form"}
                                            </button>
                                        )}
                                        {order.coc_pdf && (
                                            <button
                                                className="lo-action-btn"
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    openPdfViewer([order.coc_pdf]);
                                                }}
                                            >
                                                View COC
                                            </button>
                                        )}
                                    </div>

                                    {order.status === "rejected" && (
                                        <div className="lo-rejection-note" role="note">
                                            <strong>Rejected by the lab.</strong>{" "}
                                            {order.statusReason
                                                ? `Reason: ${order.statusReason}`
                                                : "No reason was given."}
                                        </div>
                                    )}

                                    {!isCollapsed && (
                                        <div className="lo-card-samples">
                                            {sampleList.map((sample) => (
                                                <div key={sample.sampleId} className="lo-sample-item">
                                                    <div className="lo-sample-identity">
                                                        <div className="lo-sample-name">{sample.sampleName || "—"}</div>
                                                        <div className="lo-sample-batch">{sample.batch_number || "—"}</div>
                                                    </div>
                                                    <div className="lo-sample-tests">
                                                        {sample.tests?.length === 10
                                                            ? "Full Panel"
                                                            : (sample.tests || [])
                                                                  .map((t) => capitalize(t).replace(/_/g, " "))
                                                                  .join(", ") || "—"}
                                                    </div>
                                                    {sample.coaPdfUrl && (
                                                        <button
                                                            className="lo-action-btn lo-action-btn--coa"
                                                            onClick={() => openPdfViewer([sample.coaPdfUrl])}
                                                        >
                                                            View COA
                                                        </button>
                                                    )}
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                )}
            </div>
        </div>
    );
};

export default LabOrders;
